import {DateRange, Event} from '@mui/icons-material';
import {
  BottomNavigation,
  BottomNavigationAction,
  Divider,
  alpha,
  styled,
} from '@mui/material';
import {type FC} from 'react';
import DatePicker from './DatePicker.tsx';
import useDateTimeParams from '@src/hooks/useDateTimeParams.ts';
import dimensions from '@src/styles/dimensions.ts';
import {isSingleDayDateRange} from '@src/utils/dates.ts';

const StyledBottomNavAction = styled(BottomNavigationAction)(({theme}) => ({
  border: 1,
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: 2 * Number(theme.shape.borderRadius),
  paddingBlockEnd: 2,
  maxWidth: 'max-content',

  '& .MuiBottomNavigationAction-label': {
    fontSize: 12,
    lineHeight: theme.typography.caption.lineHeight,
  },

  '& .MuiSvgIcon-root': {
    paddingBlockStart: 4,
  },

  '&.Mui-selected': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity,
    ),
    '& .MuiBottomNavigationAction-label': {
      fontSize: 12,
    },
  },
}));

const TABS = {
  SINGLE_DATE: 'single-date',
  TIMESPAN: 'timespan',
};

const Visualize: FC = () => {
  const {parsedDateTimeFrom, parsedDateTimeTo, setDateTimeParams} =
    useDateTimeParams();

  if (!parsedDateTimeFrom || !parsedDateTimeTo) {
    return null;
  }

  const isSingleDate = isSingleDayDateRange(
    parsedDateTimeFrom,
    parsedDateTimeTo,
  );
  const dateValue = isSingleDate ? TABS.SINGLE_DATE : TABS.TIMESPAN;

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    if (newValue === TABS.SINGLE_DATE) {
      setDateTimeParams({
        dateTimeFrom: parsedDateTimeTo.startOf('day').toISO(),
        dateTimeTo: parsedDateTimeTo.endOf('day').toISO(),
      });
    } else {
      setDateTimeParams({
        dateTimeFrom: parsedDateTimeTo
          .minus({months: 1})
          .startOf('day')
          .toISO(),
        dateTimeTo: parsedDateTimeTo.endOf('day').toISO(),
      });
    }
  };

  return (
    <>
      <BottomNavigation
        showLabels
        value={dateValue}
        onChange={handleTabChange}
        sx={{
          justifyContent: 'flex-start',
          gap: 2,
          marginBlock: -0.5,
        }}
      >
        <StyledBottomNavAction
          value={TABS.SINGLE_DATE}
          label="Single Date"
          icon={<Event />}
        />
        <StyledBottomNavAction
          value={TABS.TIMESPAN}
          label="Timespan"
          icon={<DateRange />}
        />
      </BottomNavigation>
      <Divider
        sx={{
          marginInline: `-${dimensions.leftSidebar.contentMargin}px`,
          marginBlock: `${dimensions.leftSidebar.contentMargin}px`,
        }}
      />
      <DatePicker />
    </>
  );
};

export default Visualize;
